import React from 'react';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { GET_LOCATION_QUERY } from 'services/api/location';
import {
  MUD_TYPES,
  RIG_TYPES,
  ROTARY_DRIVE_TYPES,
  StepProps,
  WELL_TYPES
} from '../types/types';

const Step1WellSetup: React.FC<StepProps> = ({ reportData, onChange }) => {
  const set = (field: string, value: any) =>
    onChange({ [field]: value } as any);

  const { data: locationsResponse, isLoading: locationsLoading } = useQuery(
    'locations',
    GET_LOCATION_QUERY,
    { staleTime: 5 * 60 * 1000 }
  );
  const locations = locationsResponse?.data ?? [];

  return (
    <div>
      {/* ── Drilling Contractor & Rig ────────────────────────────────────────── */}
      <Card className="pjr-section-card mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          Contractor & Rig Information
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Drilling Contractor
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. SEADRILL"
                  value={reportData.contractor}
                  onChange={e => set('contractor', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Rig Location
                </Form.Label>
                {locationsLoading ? (
                  <div className="d-flex align-items-center gap-2 py-1">
                    <Spinner animation="border" size="sm" />
                    <span className="fs-9 text-muted">
                      Loading locations...
                    </span>
                  </div>
                ) : (
                  <Form.Select
                    size="sm"
                    value={reportData.rigLocation}
                    onChange={e => set('rigLocation', e.target.value)}
                  >
                    <option value="">-- Select Location --</option>
                    {locations.map(loc => (
                      <option key={loc._id} value={loc.location}>
                        {loc.location}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Rig Type
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={reportData.rigType}
                  onChange={e => set('rigType', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {RIG_TYPES.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Rotary Drive
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={reportData.rotaryDrive}
                  onChange={e => set('rotaryDrive', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {ROTARY_DRIVE_TYPES.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Mud Type
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={reportData.mudType}
                  onChange={e => set('mudType', e.target.value)}
                >
                  {MUD_TYPES.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Well Details ─────────────────────────────────────────────────────── */}
      <Card className="pjr-section-card green mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          Well Details
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">Lease</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. OCS-G 37822"
                  value={reportData.lease}
                  onChange={e => set('lease', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">Block</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. 41"
                  value={reportData.block}
                  onChange={e => set('block', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Well No.
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  placeholder="1"
                  value={reportData.wellNumber}
                  onChange={e =>
                    set(
                      'wellNumber',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Parish / County
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. FOURCHON"
                  value={reportData.parish}
                  onChange={e => set('parish', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Well Type
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={reportData.wellType}
                  onChange={e => set('wellType', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {WELL_TYPES.map(t => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Water Depth (ft)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  placeholder="0"
                  value={reportData.waterDepth}
                  onChange={e =>
                    set(
                      'waterDepth',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Well Target
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Target formation"
                  value={reportData.wellTarget}
                  onChange={e => set('wellTarget', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Latitude
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. 27.123456"
                  value={reportData.latitude}
                  onChange={e => set('latitude', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Longitude
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. -90.123456"
                  value={reportData.longitude}
                  onChange={e => set('longitude', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Directional / Wellbore Conditions ────────────────────────────────── */}
      <Card className="pjr-section-card orange mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          Wellbore Conditions
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3 align-items-center">
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Directional?
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={reportData.wellDirectional ? 'YES' : 'NO'}
                  onChange={e =>
                    set('wellDirectional', e.target.value === 'YES')
                  }
                >
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Tight Hole?
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={reportData.tightHole ? 'YES' : 'NO'}
                  onChange={e => set('tightHole', e.target.value === 'YES')}
                >
                  <option value="NO">NO</option>
                  <option value="YES">YES</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Max DLS (deg/100ft)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.1"
                  placeholder="0.0"
                  value={reportData.maxDLS}
                  onChange={e =>
                    set(
                      'maxDLS',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Max Hole Angle (°)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.1"
                  placeholder="0.0"
                  value={reportData.maxHoleAngle}
                  onChange={e =>
                    set(
                      'maxHoleAngle',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Tool Specifications ───────────────────────────────────────────────── */}
      <Card className="pjr-section-card purple mb-3">
        <Card.Header className="py-2 fw-semibold fs-9 text-uppercase">
          Tool Specifications
        </Card.Header>
        <Card.Body className="py-3">
          <Row className="g-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Type of Job
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. UWD"
                  value={reportData.typeOfJob}
                  onChange={e => set('typeOfJob', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Tool Type
                </Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. DTU-1600"
                  value={reportData.toolType}
                  onChange={e => set('toolType', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Tool Series
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  placeholder="e.g. 16"
                  value={reportData.toolSeries}
                  onChange={e =>
                    set(
                      'toolSeries',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Tool OD (in)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.25"
                  placeholder="0"
                  value={reportData.toolOD}
                  onChange={e =>
                    set(
                      'toolOD',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Pilot Hole Dia (in)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.25"
                  placeholder="e.g. 30"
                  value={reportData.pilotHoleDia}
                  onChange={e =>
                    set(
                      'pilotHoleDia',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="fs-9 fw-semibold mb-1">
                  Opening Dia (in)
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  step="0.25"
                  placeholder="e.g. 32"
                  value={reportData.openingDia}
                  onChange={e =>
                    set(
                      'openingDia',
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Step1WellSetup;
